import React, { useEffect } from 'react';
import { ArrowLeft, Phone, Mail, MapPin } from 'lucide-react';
export function TermsPage() {
  useEffect(() => {
    document.title = 'Terms of Service | BRIXEN CREDIT LLC';
    window.scrollTo(0, 0);
  }, []);
  const scrollToSection = (e: React.MouseEvent<HTMLAnchorElement>, id: string) => {
    e.preventDefault();
    const element = document.getElementById(id);
    if (element) {
      element.scrollIntoView({
        behavior: 'smooth'
      });
    }
  };
  return <div className="min-h-screen bg-gray-50 font-sans text-gray-900" data-id="element-1187">
      {/* Header */}
      <div className="bg-white border-b border-gray-200 sticky top-0 z-10" data-id="element-1188">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-4" data-id="element-1189">
          <a href="/" className="inline-flex items-center text-gray-600 hover:text-purple-600 font-medium transition-colors" data-id="element-1190">
            <ArrowLeft className="w-5 h-5 mr-2" data-id="element-1191" />
            Back to Home
          </a>
        </div>
      </div>

      <main className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12" data-id="element-1192">
        <div className="bg-white rounded-2xl shadow-lg p-8 md:p-12" data-id="element-1193">
          {/* Page Title */}
          <div className="border-b border-gray-100 pb-8 mb-8" data-id="element-1194">
            <h1 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4" data-id="element-1195">
              Terms of Service
            </h1>
            <p className="text-sm text-gray-500 font-medium" data-id="element-1196">
              Last Updated: January 30, 2026
            </p>
          </div>

          {/* Introduction */}
          <div className="prose prose-lg max-w-none text-gray-700 mb-12" data-id="element-1197">
            <p className="mb-4" data-id="element-1198">
              By accessing this website, submitting your information, or
              enrolling in any credit restoration or consulting program offered
              by BRIXEN CREDIT LLC (hereinafter referred to as the "Company"),
              you ("Client," "you," or "your") acknowledge, understand, and
              agree to be legally bound by these Terms of Service.
            </p>
            <p className="mb-4" data-id="element-1199">
              These Terms are structured to provide full legal protection to the
              Company and to ensure compliance with all applicable federal and
              state regulations, including but not limited to the Credit Repair
              Organizations Act (CROA), the Fair Credit Reporting Act (FCRA),
              the Telemarketing Sales Rule (TSR), and Federal Trade Commission
              (FTC) guidelines.
            </p>
            <p data-id="element-1200">
              If you do not agree with these Terms, you must not use the website
              or enroll in any service.
            </p>
          </div>

          {/* Table of Contents */}
          <div className="bg-gray-50 rounded-xl p-6 mb-12" data-id="element-1201">
            <h2 className="text-lg font-bold text-gray-900 mb-4" data-id="element-1202">
              Table of Contents
            </h2>
            <nav data-id="element-1203">
              <ul className="space-y-2 text-sm md:text-base" data-id="element-1204">
                {[{
                id: 'scope',
                label: '1.1 Professional Scope of Services'
              }, {
                id: 'lpoa',
                label: '1.2 Limited Power of Attorney (LPOA) and Mandatory Reporting Authorization'
              }, {
                id: 'obligations',
                label: '1.3 Strict Client Obligations'
              }, {
                id: 'arbitration',
                label: '1.4 Binding Arbitration and Class Action Waiver'
              }, {
                id: 'sms-consent',
                label: '1.5 SMS Communications Consent'
              }, {
                id: 'no-guarantee',
                label: '2. No Guarantee of Results'
              }, {
                id: 'liability',
                label: '3. Limitation of Liability'
              }, {
                id: 'modifications',
                label: '4. Modifications to Terms'
              }, {
                id: 'governing-law',
                label: '5. Governing Law'
              }, {
                id: 'contact',
                label: 'Company Contact Information'
              }].map(item => <li key={item.id} data-id="element-1205">
                    <a href={`#${item.id}`} onClick={e => scrollToSection(e, item.id)} className="text-purple-600 hover:text-purple-800 hover:underline transition-colors" data-id="element-1206">
                      {item.label}
                    </a>
                  </li>)}
              </ul>
            </nav>
          </div>

          {/* Content Sections */}
          <div className="prose prose-lg max-w-none text-gray-700 space-y-12" data-id="element-1207">
            {/* Section 1.1 */}
            <section id="scope" className="scroll-mt-24" data-id="element-1208">
              <h2 className="text-2xl font-bold text-gray-900 mb-4" data-id="element-1209">
                1.1 Professional Scope of Services
              </h2>
              <p className="mb-4" data-id="element-1210">
                The Company acts exclusively as a professional credit consulting
                and restoration service provider. The Company does not provide
                legal advice, act as a law firm, or guarantee specific credit
                outcomes.
              </p>
              <p className="mb-4" data-id="element-1211">
                Our services include, but are not limited to:
              </p>
              <ul className="list-disc pl-6 space-y-3 mb-4" data-id="element-1212">
                <li data-id="element-1213">
                  <strong data-id="element-1214">Comprehensive Credit Audit:</strong> Performing a
                  detailed analysis of consumer credit reports obtained from
                  Equifax, Experian, and TransUnion to identify information that
                  may be inaccurate, erroneous, obsolete, misleading, or
                  unverifiable under the Fair Credit Reporting Act (FCRA).
                </li>
                <li data-id="element-1215">
                  <strong data-id="element-1216">Dispute and Validation Strategy:</strong> Drafting,
                  preparing, and managing customized dispute letters and debt
                  validation requests to be submitted to credit bureaus and
                  creditors in accordance with the FCRA and other applicable
                  consumer protection laws.
                </li>
                <li data-id="element-1217">
                  <strong data-id="element-1218">Third-Party Intervention:</strong> Sending formal
                  validation and verification requests to collection agencies,
                  original creditors, and data furnishers when legally
                  appropriate.
                </li>
                <li data-id="element-1219">
                  <strong data-id="element-1220">Federal Escalation:</strong> Preparing and submitting
                  formal consumer complaints to the Consumer Financial
                  Protection Bureau (CFPB) and the Federal Trade Commission
                  (FTC) in cases involving severe consumer rights violations,
                  inaccurate reporting, or suspected identity theft.
                </li>
                <li data-id="element-1221">
                  <strong data-id="element-1222">Credit Education:</strong> Providing general
                  educational guidance and strategic recommendations related to
                  debt management, credit behavior, and long-term credit score
                  optimization. Educational guidance does not constitute
                  financial or legal advice.
                </li>
              </ul>
              <p data-id="element-1223">
                The Client acknowledges that the Company does not control the
                decisions of credit bureaus, creditors, or collection agencies,
                and therefore cannot guarantee removals, score increases, or
                specific timelines.
              </p>
            </section>

            {/* Section 1.2 */}
            <section id="lpoa" className="scroll-mt-24" data-id="element-1224">
              <h2 className="text-2xl font-bold text-gray-900 mb-4" data-id="element-1225">
                1.2 Limited Power of Attorney (LPOA) and Mandatory Reporting
                Authorization
              </h2>
              <p className="mb-4" data-id="element-1226">
                To execute the services described above, the Client hereby
                grants the Company a Limited Power of Attorney (LPOA)
                authorizing the Company to act as the Client's authorized agent
                solely for the following purposes:
              </p>
              <ul className="list-disc pl-6 space-y-2 mb-6" data-id="element-1227">
                <li data-id="element-1228">
                  Signing dispute letters, verification requests, validation
                  letters, and related credit correspondence on the Client's
                  behalf (physically or electronically).
                </li>
                <li data-id="element-1229">
                  Communicating directly with the three major credit bureaus
                  (Equifax, Experian, TransUnion) and secondary or specialty
                  reporting agencies such as LexisNexis, Innovis, ChexSystems,
                  and similar entities.
                </li>
                <li data-id="element-1230">
                  Managing communications with creditors, debt collectors, and
                  data furnishers as part of the credit restoration process.
                </li>
              </ul>

              <h3 className="text-xl font-bold text-gray-900 mb-4" data-id="element-1231">
                FTC Identity Theft Authorization (Critical Disclosure)
              </h3>
              <p className="mb-4" data-id="element-1232">
                If, during the credit audit or dispute process, accounts are
                identified that the Client claims are fraudulent, unauthorized,
                or the result of identity theft:
              </p>
              <ul className="list-disc pl-6 space-y-2 mb-4" data-id="element-1233">
                <li data-id="element-1234">
                  The Client expressly authorizes the Company to prepare,
                  complete, and submit an Identity Theft Report and/or Identity
                  Theft Affidavit on the Client's behalf via IdentityTheft.gov,
                  administered by the Federal Trade Commission (FTC).
                </li>
                <li data-id="element-1235">
                  The Client authorizes the Company to use the Client's personal
                  information, including Social Security Number (SSN), address,
                  and date of birth, strictly for the purpose of completing
                  these federal forms.
                </li>
                <li data-id="element-1236">
                  The Client certifies under penalty of perjury that any account
                  identified as fraudulent and included in an FTC filing is, in
                  fact, unauthorized and not knowingly opened or benefited from
                  by the Client.
                </li>
                <li data-id="element-1237">
                  The Client understands that knowingly submitting false
                  information to the FTC constitutes a federal crime and agrees
                  to assume full legal responsibility for the accuracy of all
                  information provided.
                </li>
              </ul>
            </section>

            {/* Section 1.3 */}
            <section id="obligations" className="scroll-mt-24" data-id="element-1238">
              <h2 className="text-2xl font-bold text-gray-900 mb-4" data-id="element-1239">
                1.3 Strict Client Obligations
              </h2>
              <p className="mb-4" data-id="element-1240">
                The success and continuation of the credit restoration process
                require full cooperation from the Client. Failure to comply with
                any of the following obligations will result in an immediate
                suspension or termination of all services without refund.
              </p>
              <p className="mb-4" data-id="element-1241">
                Client obligations include, but are not limited to:
              </p>
              <ul className="list-disc pl-6 space-y-3 mb-4" data-id="element-1242">
                <li data-id="element-1243">
                  <strong data-id="element-1244">Mandatory Credit Monitoring:</strong> The Client must
                  maintain an active, paid subscription to MyScoreIQ or an
                  equivalent three-bureau credit monitoring service at all
                  times. If access to credit reports is lost, suspended, or
                  canceled, the Company cannot continue work on the file.
                </li>
                <li data-id="element-1245">
                  <strong data-id="element-1246">Non-Interference Policy:</strong> The Client agrees
                  NOT to contact credit bureaus, creditors, or collection
                  agencies directly while the Company is actively managing the
                  file. Unauthorized contact may reset investigation timelines
                  and materially interfere with legal dispute strategies.
                </li>
                <li data-id="element-1247">
                  <strong data-id="element-1248">Correspondence Management:</strong> The Client must
                  upload or submit any physical or digital correspondence
                  received from credit bureaus, creditors, or collection
                  agencies to the client portal within five (5) business days of
                  receipt.
                </li>
                <li data-id="element-1249">
                  <strong data-id="element-1250">Credit Management During the Process:</strong> The
                  Client agrees to refrain from applying for new credit, opening
                  new accounts, missing payments, or incurring additional
                  negative items while enrolled, as such actions may negatively
                  impact results and delay progress.
                </li>
              </ul>
            </section>

            {/* Section 1.4 */}
            <section id="arbitration" className="scroll-mt-24" data-id="element-1251">
              <h2 className="text-2xl font-bold text-gray-900 mb-4" data-id="element-1252">
                1.4 Binding Arbitration and Class Action Waiver
              </h2>
              <p className="mb-4" data-id="element-1253">
                Any dispute, claim, or controversy arising out of or relating to
                these Terms of Service, the services provided, or the
                relationship between the Client and the Company shall be
                resolved exclusively through BINDING ARBITRATION administered by
                the American Arbitration Association (AAA).
              </p>
              <ul className="list-disc pl-6 space-y-2 mb-4" data-id="element-1254">
                <li data-id="element-1255">The Client waives the right to a jury trial.</li>
                <li data-id="element-1256">
                  Arbitration shall be conducted on an individual basis only.
                </li>
                <li data-id="element-1257">
                  The Client expressly waives the right to participate in any
                  class action, collective action, or representative proceeding
                  against the Company.
                </li>
              </ul>
            </section>

            {/* Section 1.5 */}
            <section id="sms-consent" className="scroll-mt-24" data-id="element-1258">
              <h2 className="text-2xl font-bold text-gray-900 mb-4" data-id="element-1259">
                1.5 SMS Communications Consent
              </h2>
              <p className="font-medium text-gray-900" data-id="element-1260">
                By submitting a form on our website, you agree to receive SMS
                messages from Brixen Credit LLC regarding your inquiry,
                appointment reminders, account updates, and customer support.
                Message frequency may vary. Message and data rates may apply.
                Reply STOP to unsubscribe or HELP for assistance. Consent is not
                a condition of purchase.
              </p>
            </section>

            {/* Section 2 */}
            <section id="no-guarantee" className="scroll-mt-24" data-id="element-1261">
              <h2 className="text-2xl font-bold text-gray-900 mb-4" data-id="element-1262">
                2. No Guarantee of Results
              </h2>
              <p className="mb-4" data-id="element-1263">
                The Client acknowledges that credit restoration outcomes depend
                on third parties, including credit bureaus and creditors. The
                Company does not and cannot guarantee:
              </p>
              <ul className="list-disc pl-6 space-y-2 mb-4" data-id="element-1264">
                <li data-id="element-1265">Specific score increases</li>
                <li data-id="element-1266">Removal of any particular account</li>
                <li data-id="element-1267">Timelines for results</li>
              </ul>
              <p data-id="element-1268">
                All services are performed on a best-effort basis in compliance
                with applicable laws.
              </p>
            </section>

            {/* Section 3 */}
            <section id="liability" className="scroll-mt-24" data-id="element-1269">
              <h2 className="text-2xl font-bold text-gray-900 mb-4" data-id="element-1270">
                3. Limitation of Liability
              </h2>
              <p data-id="element-1271">
                To the maximum extent permitted by law, the Company shall not be
                liable for any indirect, incidental, consequential, or punitive
                damages. The Company's total liability, if any, shall not exceed
                the total amount paid by the Client for services rendered.
              </p>
            </section>

            {/* Section 4 */}
            <section id="modifications" className="scroll-mt-24" data-id="element-1272">
              <h2 className="text-2xl font-bold text-gray-900 mb-4" data-id="element-1273">
                4. Modifications to Terms
              </h2>
              <p data-id="element-1274">
                The Company reserves the right to modify or update these Terms
                of Service at any time. Continued use of the website or services
                after such updates constitutes acceptance of the revised Terms.
              </p>
            </section>

            {/* Section 5 */}
            <section id="governing-law" className="scroll-mt-24" data-id="element-1275">
              <h2 className="text-2xl font-bold text-gray-900 mb-4" data-id="element-1276">
                5. Governing Law
              </h2>
              <p data-id="element-1277">
                These Terms shall be governed by and construed in accordance
                with the laws of the State of Florida, without regard to
                conflict-of-law principles.
              </p>
            </section>

            {/* Contact Information */}
            <section id="contact" className="scroll-mt-24" data-id="element-1278">
              <h2 className="text-2xl font-bold text-gray-900 mb-8" data-id="element-1279">
                Company Contact Information
              </h2>

              <div className="bg-gray-50 rounded-xl p-8 border border-gray-100" data-id="element-1280">
                <h3 className="text-xl font-bold text-gray-900 mb-6" data-id="element-1281">
                  BRIXEN CREDIT LLC
                </h3>
                <div className="space-y-4" data-id="element-1282">
                  <div className="flex items-start" data-id="element-1283">
                    <Phone className="w-5 h-5 text-purple-600 mt-1 mr-3 flex-shrink-0" data-id="element-1284" />
                    <div data-id="element-1285">
                      <span className="block text-sm font-semibold text-gray-500 uppercase tracking-wider" data-id="element-1286">
                        Phone
                      </span>
                      <a href="tel:786-738-6588" className="text-lg text-gray-900 hover:text-purple-600 transition-colors" data-id="element-1287">
                        786-738-6588
                      </a>
                    </div>
                  </div>

                  <div className="flex items-start" data-id="element-1288">
                    <Mail className="w-5 h-5 text-purple-600 mt-1 mr-3 flex-shrink-0" data-id="element-1289" />
                    <div data-id="element-1290">
                      <span className="block text-sm font-semibold text-gray-500 uppercase tracking-wider" data-id="element-1291">
                        Email
                      </span>
                      <a href="mailto:soporte@brixencredit.com" className="text-lg text-gray-900 hover:text-purple-600 transition-colors" data-id="element-1292">
                        soporte@brixencredit.com
                      </a>
                    </div>
                  </div>

                  <div className="flex items-start" data-id="element-1293">
                    <MapPin className="w-5 h-5 text-purple-600 mt-1 mr-3 flex-shrink-0" data-id="element-1294" />
                    <div data-id="element-1295">
                      <span className="block text-sm font-semibold text-gray-500 uppercase tracking-wider" data-id="element-1296">
                        Address
                      </span>
                      <span className="text-lg text-gray-900 block" data-id="element-1297">
                        390 NE 191ST ST, MIAMI, FL 33179
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            </section>
          </div>
        </div>
      </main>
    </div>;
}